import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useSeason } from './useSeason';
import type { UseGamePositionReturn, GamePositionStats } from '../types';

export function useGamePositionPerformance(
  bowlerId: number | null,
  seasonYearOverride?: number
): UseGamePositionReturn {
  const { selectedSeason } = useSeason();
  const seasonYear = seasonYearOverride ?? selectedSeason;
  const [gameData, setGameData] = useState<GamePositionStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchGamePositionPerformance = useCallback(async () => {
    if (!bowlerId) {
      console.log('No bowler ID provided for game position analysis');
      setGameData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('Fetching game position performance for:', { bowlerId, seasonYear });

      // Skip the database function and go straight to fallback for now
      console.log('Using fallback calculation for game position performance');
      await fetchGamePositionPerformanceFallback(bowlerId, seasonYear);
      return;

      // Try to use the database function first (commented out for debugging)
      /*
      const { data, error: functionError } = await supabase
        .rpc('get_game_position_performance', {
          target_bowler_id: bowlerId,
          season_year: currentYear,
        });

      console.log('Database function result:', { data, functionError });

      if (functionError) {
        // If function doesn't exist, fall back to manual calculation
        console.warn('Game position performance function not available, using fallback calculation');
        await fetchGamePositionPerformanceFallback(bowlerId, currentYear);
        return;
      }

      const transformedData: GamePositionStats[] = (data || []).map((game: Record<string, unknown>) => ({
        game_position: game.game_position as 1 | 2 | 3,
        player_average: game.player_average as number,
        league_average: game.league_average as number,
        games_played: game.games_played as number,
        performance_difference: game.performance_difference as number,
      }));

      // Sort by game position
      transformedData.sort((a, b) => a.game_position - b.game_position);

      setGameData(transformedData);
      */
    } catch (err) {
      console.error('Error fetching game position performance:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch game position performance'));
    } finally {
      setLoading(false);
    }
  }, [bowlerId, seasonYear]);

  const fetchGamePositionPerformanceFallback = async (bowlerId: number, seasonYear: number) => {
    try {
      // Helper function to validate and extract game scores
      const extractGameScores = (row: any): Array<{ position: number; score: number }> => {
        const games: Array<{ position: number; score: number }> = [];
        
        // Check each game (1-3, since bowling typically has 3 games per series)
        for (let i = 1; i <= 3; i++) {
          const score = row[`score${i}`];
          const scoreType = row[`score_type${i}`];
          
          // Only include games with valid scores and score_type 'S' (normal score)
          if (score && score > 0 && scoreType === 'S') {
            games.push({ position: i, score });
          }
        }
        
        return games;
      };

      // Get player's performance by game position using individual game data
      const { data: playerData, error: playerError } = await supabase
        .from('league_bowler_week_summary')
        .select(`
          week_num,
          score1, score_type1,
          score2, score_type2,
          score3, score_type3
        `)
        .eq('bowler_id', bowlerId)
        .eq('season_year', seasonYear);

      if (playerError) {
        throw new Error(`Failed to fetch player game position data: ${playerError.message}`);
      }

      console.log('Player game position data:', { playerData, playerError });

      // Get league averages by game position (excluding this player)
      const { data: leagueData, error: leagueError } = await supabase
        .from('league_bowler_week_summary')
        .select(`
          score1, score_type1,
          score2, score_type2,
          score3, score_type3
        `)
        .neq('bowler_id', bowlerId)
        .eq('season_year', seasonYear);

      if (leagueError) {
        throw new Error(`Failed to fetch league game position data: ${leagueError.message}`);
      }

      console.log('League game position data count:', leagueData?.length);

      // Process the data
      const playerGameStats = new Map<number, { total: number; count: number }>();
      const leagueGameStats = new Map<number, { total: number; count: number }>();

      // Calculate player averages by game position
      playerData?.forEach((row) => {
        const games = extractGameScores(row);
        games.forEach(({ position, score }) => {
          const current = playerGameStats.get(position) || { total: 0, count: 0 };
          playerGameStats.set(position, {
            total: current.total + score,
            count: current.count + 1,
          });
        });
      });

      // Calculate league averages by game position
      leagueData?.forEach((row) => {
        const games = extractGameScores(row);
        games.forEach(({ position, score }) => {
          const current = leagueGameStats.get(position) || { total: 0, count: 0 };
          leagueGameStats.set(position, {
            total: current.total + score,
            count: current.count + 1,
          });
        });
      });

      console.log('Player game stats:', Array.from(playerGameStats.entries()));
      console.log('League game stats:', Array.from(leagueGameStats.entries()));

      // Combine the data for games 1, 2, and 3
      const gamePositionPerformance: GamePositionStats[] = [];
      
      [1, 2, 3].forEach((gamePosition) => {
        const playerStats = playerGameStats.get(gamePosition);
        const leagueStats = leagueGameStats.get(gamePosition);
        
        if (playerStats && leagueStats && leagueStats.count > 0) {
          const playerAverage = playerStats.total / playerStats.count;
          const leagueAverage = leagueStats.total / leagueStats.count;
          const performanceDifference = playerAverage - leagueAverage;

          gamePositionPerformance.push({
            game_position: gamePosition as 1 | 2 | 3,
            player_average: Math.round(playerAverage * 100) / 100,
            league_average: Math.round(leagueAverage * 100) / 100,
            games_played: playerStats.count,
            performance_difference: Math.round(performanceDifference * 100) / 100,
          });
        } else if (playerStats) {
          // Player has data but no league comparison available
          const playerAverage = playerStats.total / playerStats.count;
          
          gamePositionPerformance.push({
            game_position: gamePosition as 1 | 2 | 3,
            player_average: Math.round(playerAverage * 100) / 100,
            league_average: 0,
            games_played: playerStats.count,
            performance_difference: 0,
          });
        }
      });

      setGameData(gamePositionPerformance);
      console.log('Final game position performance:', gamePositionPerformance);
    } catch (err) {
      console.error('Error in game position performance fallback:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch game position performance data'));
      setGameData([]);
    }
  };

  const refetch = async () => {
    await fetchGamePositionPerformance();
  };

  useEffect(() => {
    fetchGamePositionPerformance();
  }, [fetchGamePositionPerformance]);

  return {
    gameData,
    loading,
    error,
    refetch,
    clearError: () => setError(null),
    retryLastOperation: refetch,
  };
}